import React, { FC, ReactNode, useEffect, useState } from "react";
import classNames from "classnames";
import { scoreCls, isAttemptPending } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import type { PdpUrlScore } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import { IVideo } from "../../hooks/useFetchVideos";
import {
  VideoPanel,
  PanelAction,
  IconAlertGlyph,
  IconClockGlyph,
  IconChartGlyph,
  IconGoToTabGlyph,
  IconLinkGlyph,
  IconChevronRightGlyph,
} from "../panels";
import { ScoreState, StateChip, RetryButton, PollIndicator } from "./ScoreState";
import { ScoreTrack } from "./ScoreTrack";
import { ScoreTrend } from "./ScoreTrend";
import { NoPageRegistered } from "./AddPageAction";
import { NotScoredYet } from "./ScoreNowAction";
import { pageAssignment } from "../pdpAssignment";
import {
  TREND_MIN_POINTS,
  bandWord,
  failedSinceScore,
  humanReason,
  isStalled,
  minutesSince,
  monthsOutOfDate,
  movement,
  nextScoreMonth,
  scoredDate,
  scoredMonth,
  subDelta,
  subPrevious,
  trendPoints,
  urlHost,
  urlHostPath,
  usePageScore,
} from "./pageScore";

/**
 * Page score — the page's first section, ahead of Performance.
 *
 * It measures a different object from Performance (the product page, not the
 * video), so it is a sibling panel rather than a fallback inside that one, with
 * its own anchor in the jump rail.
 *
 * Eight states, all distinguishable, error and not-scored above all. One score
 * point has to look FINISHED: the geometry comes from the 0–100 scale, not from
 * history, so a single reading already has shape. Movement arrives at two
 * points as a delta and a ghost marker; a trend replaces the track at four.
 */

const MICRO = "font-CircularStdBlack uppercase text-muted";
const MICRO_STYLE = { fontSize: 9.5, letterSpacing: ".12em" } as const;

const Micro: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div className={classNames(MICRO, className)} style={MICRO_STYLE}>
    {children}
  </div>
);

const Divider: FC<{ tight?: boolean }> = ({ tight }) => (
  <div className="border-t border-border-token" style={{ marginTop: tight ? 14 : 18, marginBottom: tight ? 14 : 18 }} />
);

/** h24 pill, band colour on its own 8% tint — the word the colour finally gets. */
const BandChip: FC<{ score: number | null }> = ({ score }) => (
  <span
    className={classNames("inline-flex items-center h-6 px-2.5 rounded-full font-CircularStdBlack shrink-0", scoreCls(score))}
    style={{ fontSize: 12, background: "color-mix(in srgb, currentColor 8%, transparent)" }}
  >
    {bandWord(score)}
  </span>
);

/** ▲ 6 / ▼ 3 on --up / --down — direction and level are different questions, so never band colours. */
const DeltaChip: FC<{ change: number; small?: boolean }> = ({ change, small }) => {
  if (change === 0) return null;
  const up = change > 0;
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full font-CircularStdBlack tabular-nums shrink-0",
        up ? "bg-up-bg text-up" : "bg-down-bg text-down",
        small ? "h-5 px-1.5" : "h-6 px-2",
      )}
      style={{ fontSize: small ? 10.5 : 11 }}
    >
      <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true" style={{ transform: up ? "none" : "rotate(180deg)" }}>
        <polygon points="4,1 7.5,7 0.5,7" style={{ fill: "currentColor" }} />
      </svg>
      {Math.abs(change)}
    </span>
  );
};

/** The four movement forms — all four are the Scored state. */
const MovementLine: FC<{ history?: PdpUrlScore[] | null }> = ({ history }) => {
  // null = the series hasn't loaded (or couldn't): say nothing rather than
  // mislabel a rescored page as a first score. An empty or one-item array is
  // a genuine first score.
  if (!history) return null;
  const m = movement(history);
  if (m.kind === "first") {
    return (
      <span className="text-text-2" style={{ fontSize: 12 }}>
        First score — a trend appears as it&apos;s rescored
      </span>
    );
  }
  if (m.kind === "zero") {
    return (
      <span className="text-text-2" style={{ fontSize: 12 }}>
        <span className="font-CircularStdBlack text-text-1">No change</span> since {m.since}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      <DeltaChip change={m.change} />
      <span className="text-text-2 tabular-nums" style={{ fontSize: 12 }}>
        {m.change > 0 ? "up" : "down"} from {m.from} {m.sameMonth ? "on" : "in"} {m.since}
      </span>
    </span>
  );
};

interface SubProps {
  label: string;
  sentence: string;
  score: number | null;
  delta: number | null;
  previous: number | null;
  nested: boolean;
  divided?: boolean;
}

/**
 * A subscore. Two columns at ≥sm: micro-label, value, band word, track,
 * sentence. Below sm the number leads and the label rides beside it in
 * sentence case — flush-right left ~250px of void between the two at 375 and
 * they stopped reading as one object. Nested hosts keep a two-row ledger with
 * no track and no sentence: nothing that carries a number is removed.
 */
const SubScore: FC<SubProps> = ({ label, sentence, score, delta, previous, nested, divided }) => {
  if (nested) {
    return (
      <div className="flex items-center justify-between gap-3 py-2 border-b border-border-token last:border-b-0">
        <span className="text-text-2" style={{ fontSize: 12.5 }}>
          {label}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className={classNames("font-CircularStdBlack tabular-nums", scoreCls(score))} style={{ fontSize: 15 }}>
            {score == null ? "—" : score}
          </span>
          {delta != null && <DeltaChip change={delta} small />}
        </span>
      </div>
    );
  }
  return (
    <div className={classNames("min-w-0", divided && "sm:border-l sm:border-border-token sm:pl-5")}>
      {/* The micro-label is a COLUMN heading — .12em tracking, sitting over the
          value it names. Below sm this is no longer a column, so the heading
          gives way to the sentence-case label beside the number. */}
      <Micro className="hidden sm:block">{label}</Micro>
      <span className="flex items-baseline gap-2 flex-wrap sm:mt-2">
        <span
          className={classNames("font-CircularStdBlack tabular-nums leading-none text-[18px] sm:text-[22px]", scoreCls(score))}
        >
          {score == null ? "—" : score}
        </span>
        <span className="sm:hidden text-text-2" style={{ fontSize: 12.5 }}>
          {label}
        </span>
        <span className={classNames("hidden sm:inline", scoreCls(score))} style={{ fontSize: 12 }}>
          {bandWord(score)}
        </span>
        {delta != null && <DeltaChip change={delta} small />}
      </span>
      {score != null && (
        <span className={classNames("block", scoreCls(score))} style={{ marginTop: 8 }}>
          <ScoreTrack value={score} previous={previous} size="sub" />
        </span>
      )}
      <div className="hidden sm:block text-text-2" style={{ fontSize: 12, marginTop: 8 }}>
        {sentence}
      </div>
    </div>
  );
};

/** Mirrors the Scored layout so loading → scored is shift-free. */
const ScoredSkeleton: FC<{ nested: boolean }> = ({ nested }) => {
  const bar = (w: number | string, h: number, extra = "") => (
    <div className={classNames("bg-track animate-pulse motion-reduce:animate-none rounded", extra)} style={{ width: w, height: h }} />
  );
  return (
    <div className="flex flex-col" aria-hidden="true">
      {bar(56, 9)}
      <div className="flex items-end gap-3" style={{ marginTop: 8 }}>
        {bar(96, nested ? 32 : 44, "rounded-lg")}
        {bar(70, 24, "rounded-full")}
      </div>
      {bar("100%", 6, "rounded-full mt-4")}
      {!nested && bar("100%", 10, "mt-2")}
      <Divider />
      {nested ? (
        <div className="flex flex-col gap-3">
          {bar("100%", 18)}
          {bar("100%", 18)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {[0, 1].map((i) => (
            <div key={i} className="flex flex-col gap-2">
              {/* Correction 7 drops the micro-label and the sentence below sm,
                  so the skeleton drops them too — otherwise loading → scored
                  shifts ~30px per subscore at 375. */}
              <span className="hidden sm:block">{bar(120, 9)}</span>
              {bar(64, 22, "rounded-lg")}
              {bar("100%", 5, "rounded-full")}
              <span className="hidden sm:block">{bar("70%", 11)}</span>
            </div>
          ))}
        </div>
      )}
      <Divider />
      <div className="flex justify-between gap-3">
        {bar(180, 12)}
        {bar(160, 12)}
      </div>
    </div>
  );
};

/** The strip that sits above a readable-but-old score: state 8, and the stalled variant. */
const NoticeStrip: FC<{
  tone: "down" | "tint";
  title: string;
  body: ReactNode;
  action?: ReactNode;
}> = ({ tone, title, body, action }) => (
  <div
    className={classNames(
      "flex items-center justify-between gap-3 flex-wrap rounded-xl px-4 py-3",
      tone === "down" ? "bg-down-bg" : "bg-tint",
    )}
    style={{ marginBottom: 18 }}
  >
    <div className="min-w-0">
      <div className="font-CircularStdBlack text-text-1" style={{ fontSize: 13 }}>
        {title}
      </div>
      <div className="text-text-2" style={{ fontSize: 12, marginTop: 2 }}>
        {body}
      </div>
    </div>
    {action}
  </div>
);

interface Props {
  video: IVideo;
  brandId?: number;
  onCount?: (n: number) => void;
  /** The expert / product / story hosts: compact numbers, ledger subscores. */
  nested?: boolean;
  /** Bumped by the host page when the settings modal closes — an assignment may have changed. */
  reloadKey?: number;
}

export const PageScorePanel: FC<Props> = ({ video, brandId, onCount, nested = false, reloadKey = 0 }) => {
  const { status, row, rows, scoredRows, history, reload, refresh } = usePageScore(video, brandId, reloadKey);
  // Set by Action B on success: the "Page added" strip confirms the durable
  // half of the write while the score itself is still pending. Cleared once the
  // attempt resolves either way.
  const [justAdded, setJustAdded] = useState<string | null>(null);
  // Action A in flight — the header says "Scoring" while the block works.
  const [scoring, setScoring] = useState(false);

  useEffect(() => {
    if (status === "ready") onCount?.(scoredRows.length);
  }, [status, scoredRows.length, onCount]);

  const product = (video?.videoProducts || []).find((p: any) => p?.desireid);
  const score = row?.latest_score ?? null;
  const attempt = row?.latest_attempt ?? null;
  const scored = !!score && score.overall_score != null;
  const pending = isAttemptPending(attempt);
  const stalled = isStalled(attempt);
  const failed = failedSinceScore(score, attempt);
  const points = trendPoints(history);
  const trend = points.length >= TREND_MIN_POINTS;
  const others = scoredRows.filter((r) => r !== row);
  // The page the panel speaks for is the one assigned to this video when there
  // is one (rankRows leads with it). Otherwise it is the product's best page —
  // a PRODUCT fact standing in for a video fact, which the strip says out loud.
  const assigned = !!row && pageAssignment(row, video?.id) === "mine";

  useEffect(() => {
    if (justAdded && (scored || failed || stalled)) setJustAdded(null);
  }, [justAdded, scored, failed, stalled]);

  // ---- header chip: the only place the header says something the body cannot ----
  let badge: ReactNode = null;
  if (status === "error") badge = <StateChip tone="down">Unavailable</StateChip>;
  else if (scoring) badge = <StateChip tone="tint">Scoring</StateChip>;
  else if (scored && failed) badge = <StateChip tone="down">Not current</StateChip>;
  else if (stalled) badge = <StateChip tone="tint">Delayed</StateChip>;
  else if (pending) badge = <StateChip tone="tint">Queued</StateChip>;

  const pageCount =
    rows.length > 1 ? (
      <div className="text-text-2" style={{ fontSize: 11.5, marginTop: 10 }}>
        {scoredRows.length} of {rows.length} pages for this product scored
      </div>
    ) : null;

  // One sentence with two homes, on every state that shows a page's score or
  // its scoring progress; never on the empty states, whose Action B creates AND
  // assigns. Assigning happens in the settings modal, so this points there
  // rather than opening it — the same door Action B's helper already names.
  const unassignedReason = video?.final
    ? "Assign a page under Edit settings → Product Pages and its score shows here."
    : "Pages can be assigned once the video is approved.";
  // "Showing <url>" survives only where nothing else on the state names the
  // page: the scored panel drops it because its own provenance link sits on the
  // same row, but the nested footer carries the date alone, so it keeps it.
  const unassignedShowing = row ? `Showing ${urlHostPath(row.pdp_url)}. ` : "";
  const unassigned = !!row && !assigned;

  // Above the not-scored and scoring states, which have no provenance row to
  // sit in. Still a strip, but one line of copy rather than a title over a
  // body — it introduces an empty state, so it can afford no more weight.
  const unassignedStrip = unassigned ? (
    <div className="rounded-xl bg-tint px-4 py-3 text-text-2" style={{ fontSize: 12.5, marginBottom: 18 }}>
      <span className="font-CircularStdBlack text-text-1">No page assigned to this video.</span> {unassignedShowing}
      {unassignedReason}
    </div>
  ) : null;

  // The scored state's copy of it: last, inside the provenance block, quiet.
  // A filled slab above the score was heavier than the score it introduced and
  // repeated both the header host pill and the footer link — the score leads.
  const unassignedLine = unassigned ? (
    <div className="text-text-2" style={{ fontSize: 12 }}>
      No page assigned to this video. {nested ? unassignedShowing : ""}
      {unassignedReason}
    </div>
  ) : null;

  const body = () => {
    // 1 · loading — mirrors the scored layout, heading rendered immediately.
    if (status === "loading") return <ScoredSkeleton nested={nested} />;

    // 7 · error — four simultaneous differences from Not scored, so the two can
    // never be confused: solid --down-bg, alert triangle, an Unavailable chip,
    // and a Retry. The copy refuses to imply the page is unscored.
    if (status === "error") {
      return (
        <ScoreState
          skin="error"
          icon={<IconAlertGlyph size={20} strokeWidth={1.7} />}
          title="Couldn't load the page score"
          body="Something went wrong on our end. This page may well be scored."
          action={<RetryButton onClick={reload} />}
          compact={nested}
        />
      );
    }

    // 5 · no page registered / 6 · no product at all.
    if (!row) {
      if (product) {
        return (
          <NoPageRegistered
            video={video}
            brandId={brandId}
            product={product as { desireid?: string; title?: string; url?: string | null }}
            compact={nested}
            onAdded={(url) => {
              setJustAdded(url);
              refresh();
            }}
          />
        );
      }
      return (
        <ScoreState
          skin="empty"
          icon={<span className="text-muted"><IconChartGlyph size={20} strokeWidth={1.6} /></span>}
          title="No product linked"
          body="This video isn't linked to a product, so there's no page to score."
          compact={nested}
        />
      );
    }

    if (!scored) {
      // 3 · scoring in progress — the only reachable pending state, and where
      // the async trigger lands. The poll is the visible contract.
      if (pending) {
        return (
          <div className="flex flex-col">
            {unassignedStrip}
            {justAdded && (
              // The durable half of the write is confirmed here — the page is
              // registered, whatever the score does next.
              <div className="rounded-xl bg-up-bg px-4 py-3 text-text-1" style={{ fontSize: 12.5, marginBottom: 14 }}>
                <span className="font-CircularStdBlack">Page added.</span> {urlHostPath(justAdded)} is now scored on a regular schedule.
              </div>
            )}
            <ScoreState
              skin="working"
              icon={<IconClockGlyph size={20} strokeWidth={1.7} />}
              title="Scoring in progress"
              body="This usually takes a minute or two. The score will appear here on its own."
              footer={
                <div className="flex justify-center">
                  <PollIndicator />
                </div>
              }
              compact={nested}
            />
          </div>
        );
      }
      // 4 · not scored yet, and its stalled variant — both host Action A for
      // the roles that can call it. The page count renders here too: the
      // stamp outranks "has a score" in row selection, so this can appear on
      // a video whose page IS scored.
      return (
        <div className="flex flex-col">
          {unassignedStrip}
          <NotScoredYet
            row={row}
            productId={Number(row.desireid ?? product?.desireid) || null}
            compact={nested}
            stalled={stalled}
            startedMinutesAgo={minutesSince(attempt?.created_at)}
            pageCount={pageCount}
            onCheckAgain={reload}
            onScored={refresh}
            onScoringChange={setScoring}
          />
        </div>
      );
    }

    // 2 · scored — plus the state-8 / stalled strips above a readable old score.
    const overall = score.overall_score as number;
    const human = score.human_conversion_score ?? null;
    const agentic = score.agentic_commerce_score ?? null;
    const reason = humanReason(attempt?.error);
    const footerDate = (() => {
      if (failed) return `Scored ${scoredMonth(score.scored_at)} · ${monthsOutOfDate(score.scored_at) || 1} month${monthsOutOfDate(score.scored_at) > 1 ? "s" : ""} out of date`;
      if (row.active === false || nested) return `Scored ${scoredDate(score.scored_at)}`;
      return `Scored ${scoredDate(score.scored_at)} · next score in ${nextScoreMonth(score.scored_at)}`;
    })();

    return (
      <div className="flex flex-col">
        {failed && (
          <NoticeStrip
            tone="down"
            title="Last scoring attempt failed"
            body={
              <>
                {reason.text}
                {attempt?.created_at ? ` on ${scoredDate(attempt.created_at)}` : ""}. Showing the previous score.
                {!reason.known && attempt?.error ? (
                  <span className="block text-muted" style={{ fontSize: 10.5, marginTop: 2 }}>
                    {attempt.error}
                  </span>
                ) : null}
              </>
            }
            action={<RetryButton onClick={reload} />}
          />
        )}
        {!failed && stalled && (
          <NoticeStrip
            tone="tint"
            title="Scoring is taking longer than expected"
            body={`Started ${minutesSince(attempt?.created_at)} minutes ago. Showing the previous score.`}
            action={<RetryButton onClick={reload} label="Check again" />}
          />
        )}

        {/* OVERALL — the headline; the trend replaces its track at four points. */}
        <div className={classNames(trend && !nested && "grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_2fr] gap-6")}>
          <div>
            <Micro>Overall</Micro>
            <div className="flex items-baseline gap-2.5 flex-wrap" style={{ marginTop: 6 }}>
              <span
                className={classNames(
                  "font-CircularStdBlack tabular-nums leading-none",
                  scoreCls(overall),
                  nested ? "text-[32px]" : "text-[38px] sm:text-[44px]",
                )}
              >
                {overall}
              </span>
              <span className="text-text-2" style={{ fontSize: 13 }}>
                / 100
              </span>
              <BandChip score={overall} />
              {/* Inline only where the row cannot wrap. Below sm the trend grid
                  is one column and the nested row wraps, which would drop the
                  movement sentence between the number and its scale — correction
                  6's split, arrived at from the other direction. */}
              {(trend || nested) && (
                <span className="hidden sm:inline-flex">
                  <MovementLine history={history} />
                </span>
              )}
            </div>
            {(trend || nested) && (
              <div className="sm:hidden" style={{ marginTop: 10 }}>
                <MovementLine history={history} />
              </div>
            )}
            {!trend && (
              <span className={classNames("block", scoreCls(overall))} style={{ marginTop: 12 }}>
                <ScoreTrack
                  value={overall}
                  previous={movement(history).kind === "delta" ? (movement(history) as { from: number }).from : null}
                  size="overall"
                  axis={nested ? "none" : "full"}
                />
              </span>
            )}
            {/* Movement reads AFTER the scale it moves along: number, band word,
                track, axis, then this. Putting it above the track — the obvious
                place, next to the number it talks about — splits the number
                from its own scale, so it stays below at every width. */}
            {!trend && !nested && (
              <div style={{ marginTop: 10 }}>
                <MovementLine history={history} />
              </div>
            )}
          </div>
          {trend && (
            <div className={classNames("min-w-0", nested && "mt-4")}>
              <div className="flex items-baseline justify-between gap-3">
                <Micro>Overall, by month</Micro>
                <span className="text-text-2 tabular-nums" style={{ fontSize: 11.5 }}>
                  {points.length} scores
                </span>
              </div>
              <span className={classNames("block", scoreCls(overall))} style={{ marginTop: 8 }}>
                <ScoreTrend points={points} />
              </span>
            </div>
          )}
        </div>

        <Divider tight={nested} />

        {nested ? (
          <div>
            <SubScore label="Human" sentence="" score={human} delta={subDelta(history, "human_conversion_score")} previous={null} nested />
            <SubScore label="Agentic" sentence="" score={agentic} delta={subDelta(history, "agentic_commerce_score")} previous={null} nested />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <SubScore
              label="Human conversion"
              sentence="Will a person buy from this page."
              score={human}
              delta={subDelta(history, "human_conversion_score")}
              previous={subPrevious(history, "human_conversion_score")}
              nested={false}
            />
            <SubScore
              label="Agentic commerce"
              sentence="Can an AI shopping agent parse and trust it."
              score={agentic}
              delta={subDelta(history, "agentic_commerce_score")}
              previous={subPrevious(history, "agentic_commerce_score")}
              nested={false}
              divided
            />
          </div>
        )}

        {/* Multiple scored pages — never averaged. The headline is one page;
            the rest list beneath with host, band dot and score, each linking out. */}
        {others.length > 0 && (
          <>
            <Divider tight={nested} />
            <Micro>Scored pages</Micro>
            <div className="flex flex-col" style={{ marginTop: 4 }}>
              {others.map((r) => {
                const s = r.latest_score?.overall_score ?? null;
                return (
                  <a
                    key={r.id}
                    href={r.pdp_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2.5 py-2.5 border-b border-border-token last:border-b-0 text-text-1 hover:text-accent-text"
                  >
                    <span className="flex-1 min-w-0 truncate" style={{ fontSize: 13 }}>
                      {urlHostPath(r.pdp_url)}
                    </span>
                    <span className={classNames("inline-flex items-center gap-1.5", scoreCls(s))}>
                      <span className="rounded-full" style={{ width: 6, height: 6, background: "currentColor" }} aria-hidden="true" />
                      <span className="font-CircularStdBlack tabular-nums" style={{ fontSize: 13 }}>
                        {s == null ? "—" : s}
                      </span>
                    </span>
                    <span className="text-muted"><IconChevronRightGlyph size={14} strokeWidth={2} /></span>
                  </a>
                );
              })}
            </div>
          </>
        )}

        <Divider tight={nested} />

        {/* Provenance: which page, when it was scored — and, when it isn't this
            video's own page, the line saying so, above the link it qualifies. */}
        <div className="flex flex-col gap-2">
          {unassignedLine}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {!nested && (
              <a
                href={row.pdp_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-accent-text hover:underline min-w-0 truncate"
                style={{ fontSize: 12.5 }}
                title={row.pdp_url}
              >
                <IconLinkGlyph size={13} strokeWidth={1.8} />
                <span className="truncate">{urlHostPath(row.pdp_url)}</span>
              </a>
            )}
            <span className="text-text-2 tabular-nums" style={{ fontSize: 12 }}>
              {footerDate}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <VideoPanel
      id="panel-page-score"
      title="Page score"
      count={scoredRows.length >= 2 ? scoredRows.length : undefined}
      badge={badge}
      action={
        row?.pdp_url ? (
          <a href={row.pdp_url} target="_blank" rel="noreferrer" className="shrink-0">
            <PanelAction
              label={nested ? "" : urlHost(row.pdp_url)}
              icon={<IconGoToTabGlyph size={12} strokeWidth={2} />}
              title={row.pdp_url}
            />
          </a>
        ) : undefined
      }
    >
      {body()}
    </VideoPanel>
  );
};

export default PageScorePanel;
